import React, { Suspense, lazy, useState, useCallback } from 'react';
import { useAppStore, selectActiveScreen, selectTheme, selectShowLoadDemo } from '../../store';
import StatusBar from './StatusBar';
import ErrorBoundary from '../shared/ErrorBoundary';
import { useAppLock, LockMenu } from '../AppLock';
import { FeedbackForm, FeedbackButton } from '../FeedbackForm/FeedbackForm';
import { AboutModal, buildAboutConfig } from '@ascend/about-modal';
import appIdentity from 'virtual:app-identity';
import type { UserFeedback } from '../../utils/userFeedback';
import btn from '../../styles/button.module.css';
import lay from '../../styles/layout.module.css';
import { Lock, Unlock, Play, Settings, Moon, Sun } from 'lucide-react';

const AnalysisScreen = lazy(() => import('../analysis/AnalysisScreen'));
const WarehouseScreen = lazy(() => import('../warehouse/WarehouseScreen'));
const SlottingScreen = lazy(() => import('../slotting/SlottingScreen'));
const DashboardScreen = lazy(() => import('../dashboard/DashboardScreen'));
const ReferenceScreen = lazy(() => import('../reference/ReferenceScreen'));

const SettingsModal = lazy(() => import('./SettingsModal'));
const ImportWizard = lazy(() => import('./ImportWizard'));
const SkuDetailDrawer = lazy(() => import('./SkuDetailDrawer'));
const HelpModal = lazy(() => import('./HelpModal'));
const DemoPickerModal = lazy(() => import('./DemoPickerModal'));

/** About-modal identity, derived from app.toml via the appIdentityPlugin virtual module. */
const aboutConfig = buildAboutConfig(appIdentity, 'VITE_APP_');

/** Suspense fallback spinner */
const LoadingSpinner: React.FC = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}>
    <div style={{ fontSize: 'var(--font-size-lg)', color: 'var(--text-secondary)' }}>Loading…</div>
  </div>
);

const TABS = [
  { id: 'analysis', label: 'Analysis' },
  { id: 'warehouse', label: 'Warehouse' },
  { id: 'slotting', label: 'Slotting' },
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'reference', label: 'Reference' },
];

const AppShell: React.FC = () => {
  const activeScreen = useAppStore(selectActiveScreen);
  const theme = useAppStore(selectTheme);
  const showLoadDemo = useAppStore(selectShowLoadDemo);
  const setActiveScreen = useAppStore((s) => s.setActiveScreen);
  const toggleTheme = useAppStore((s) => s.toggleTheme);
  const setSettingsOpen = useAppStore((s) => s.setSettingsOpen);
  const setHelpOpen = useAppStore((s) => s.setHelpOpen);
  const setDemoOpen = useAppStore((s) => s.setDemoOpen);
  const settingsOpen = useAppStore((s) => s.settingsOpen);
  const importWizardOpen = useAppStore((s) => s.importWizardOpen);
  const helpOpen = useAppStore((s) => s.helpOpen);
  const demoOpen = useAppStore((s) => s.demoOpen);
  const skuDrawerOpen = useAppStore((s) => s.skuDrawerOpen);
  const { lock, isEnabled, enableEncryption } = useAppLock();
  const [lockMenuOpen, setLockMenuOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [feedbackConfig, setFeedbackConfig] = useState<{
    type?: UserFeedback['type'];
    category?: string;
    error?: string;
  }>({});

  const handleOpenFeedback = useCallback((
    type?: UserFeedback['type'],
    category?: string,
    error?: string,
  ) => {
    setFeedbackConfig({ type, category, error });
    setFeedbackOpen(true);
  }, []);

  const handleCloseFeedback = useCallback(() => {
    setFeedbackOpen(false);
    setFeedbackConfig({});
  }, []);

  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Hash routing
  React.useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash.slice(1);
      if (hash && TABS.some((t) => t.id === hash)) {
        setActiveScreen(hash);
      }
    };
    handleHash();
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, [setActiveScreen]);

  const handleTabClick = (tabId: string) => {
    setActiveScreen(tabId);
    window.location.hash = tabId;
  };

  const renderScreen = () => {
    const wrap = (node: React.ReactNode) => (
      <ErrorBoundary onReportError={(err) => handleOpenFeedback('bug_report', 'error', err.message)}>
        <Suspense fallback={<LoadingSpinner />}>{node}</Suspense>
      </ErrorBoundary>
    );
    switch (activeScreen) {
      case 'analysis': return wrap(<AnalysisScreen />);
      case 'warehouse': return wrap(<WarehouseScreen />);
      case 'slotting': return wrap(<SlottingScreen />);
      case 'dashboard': return wrap(<DashboardScreen />);
      case 'reference': return wrap(<ReferenceScreen />);
      default: return wrap(<AnalysisScreen />);
    }
  };

  return (
    <div className={lay.shell}>
      {/* Header */}
      <header className={`${lay.header} app-header`}>
        <div className={lay.headerLogo}>
          Slot Matrix
        </div>

        {/* Tab bar */}
        <nav className={lay.headerNav} role="tablist" aria-label="Main navigation">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              aria-label={`${tab.label} tab`}
              aria-selected={activeScreen === tab.id}
              role="tab"
              className={activeScreen === tab.id ? btn.tabActive : btn.tabInactive}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Header icons */}
        <div className={lay.headerIcons}>
          {isEnabled && lock ? (
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setLockMenuOpen(!lockMenuOpen)}
                aria-label="Lock controls"
                title="Lock Controls"
                className={btn.icon}
              >
                <Lock size={16} />
              </button>
              {lockMenuOpen && (
                <LockMenu lock={lock} onClose={() => setLockMenuOpen(false)} />
              )}
            </div>
          ) : !isEnabled ? (
            <button
              onClick={enableEncryption}
              title="Enable password protection"
              className={btn.icon}
              aria-label="Enable password protection"
            >
              <Unlock size={16} />
            </button>
          ) : null}
          {showLoadDemo && (
            <button
              onClick={() => setDemoOpen(true)}
              aria-label="Open demo datasets"
              title="Demo Datasets"
              className={btn.icon}
            >
              <Play size={16} />
            </button>
          )}
          <button
            onClick={() => setSettingsOpen(true)}
            aria-label="Open settings"
            title="Settings"
            className={btn.icon}
          >
            <Settings size={16} />
          </button>
          <button
            onClick={toggleTheme}
            aria-label={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
            title="Toggle theme"
            className={btn.icon}
          >
            {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
          </button>
          <button
            onClick={() => setHelpOpen(true)}
            aria-label="Open help"
            title="Help"
            className={btn.icon}
          >
            ?
          </button>
        </div>
      </header>

      {/* Content */}
      <main className={lay.content}>
        {renderScreen()}
      </main>

      {/* Status bar */}
      <StatusBar />

      {/* Global overlays */}
      {settingsOpen && (
        <Suspense fallback={null}>
          <SettingsModal onOpenAbout={() => { setSettingsOpen(false); setAboutOpen(true); }} />
        </Suspense>
      )}
      {importWizardOpen && <Suspense fallback={null}><ImportWizard /></Suspense>}
      {skuDrawerOpen && <Suspense fallback={null}><SkuDetailDrawer /></Suspense>}
      {helpOpen && <Suspense fallback={null}><HelpModal /></Suspense>}
      {demoOpen && <Suspense fallback={null}><DemoPickerModal /></Suspense>}
      <AboutModal isOpen={aboutOpen} onClose={() => setAboutOpen(false)} config={aboutConfig} />
      <FeedbackForm
        isOpen={feedbackOpen}
        onClose={handleCloseFeedback}
        initialType={feedbackConfig.type}
        initialCategory={feedbackConfig.category}
        initialError={feedbackConfig.error}
      />
      <FeedbackButton onOpen={handleOpenFeedback} />
    </div>
  );
};

export default AppShell;
